// The Suggested tab of the Disciplinary page: reported incidents with the step
// the agreement suggests, incident cases in progress, and the cases the nightly
// checks raised (late handover, breach week, reckless driving, missed service)
// that nobody has released yet. db/migrations/4401, docs/DISCIPLINARY-INCIDENTS.md.
//
// Asked for by Luyanda on 5 October 2026: the automated parts of the system
// suggest a disciplinary step, and a manager releases it. Releasing is giving
// the driver the notice in writing (clause 27.4), never the sanction: the
// sanction comes after he has been heard, on the case's own page.
import { useState } from 'react'
import { Bot, ExternalLink, Send, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useDismissAutomatedSuggestion, useReleaseAutomatedSuggestion } from '../../hooks/backend/disciplinary'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { writeErrorMessage } from '../../utils/writeError'
import { day, todaySast, when } from '../../utils/disciplinary'
import { NOTICE_METHODS, stepTone, type AutomatedSuggestion, type SuggestionsPage } from '../../utils/disciplinaryIncidents'
import { IncidentCard } from './IncidentCard'

export function SuggestionsPanel({ page, onChanged }: { page: SuggestionsPage; onChanged: () => void }) {
  const waiting = page.incidents.filter((i) => i.status === 'reported')
  const going = page.incidents.filter((i) => i.status === 'released' && i.case && !['decided', 'withdrawn'].includes(i.case.status))
  const done = page.incidents.filter((i) => i.status === 'closed' || (i.case && ['decided', 'withdrawn'].includes(i.case.status)))
  const offCode = page.gate.drivers - page.gate.on_code

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground"><Bot className="h-4 w-4" /> From the nightly checks ({page.automated.length})</h2>
        {page.automated.length === 0 ? (
          <p className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            Nothing is waiting from the checks on late handovers, driving and missed services.
            {offCode > 0 && ` ${offCode} of the ${page.gate.drivers} drivers ${offCode === 1 ? 'has' : 'have'} not signed v6 or Addendum No. 1, so for them those checks are coaching only and raise no case until they sign.`}
          </p>
        ) : (
          page.automated.map((a) => <AutomatedCard key={a.id} a={a} onChanged={onChanged} />)
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Reported incidents waiting for a decision ({waiting.length})</h2>
        {waiting.length === 0
          ? <p className="text-sm text-muted-foreground">No reported incident is waiting.</p>
          : waiting.map((i) => <IncidentCard key={i.id} i={i} onChanged={onChanged} compact />)}
      </section>

      {going.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">In progress ({going.length})</h2>
          {going.map((i) => <IncidentCard key={i.id} i={i} onChanged={onChanged} compact />)}
        </section>
      )}

      {done.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">Closed or decided in the last 90 days ({done.length})</h2>
          {done.map((i) => <IncidentCard key={i.id} i={i} onChanged={onChanged} compact />)}
        </section>
      )}
    </div>
  )
}

function AutomatedCard({ a, onChanged }: { a: AutomatedSuggestion; onChanged: () => void }) {
  const release = useReleaseAutomatedSuggestion<{ page?: string }>()
  const dismiss = useDismissAutomatedSuggestion()
  const [open, setOpen] = useState<null | 'release' | 'dismiss'>(null)
  const [notice, setNotice] = useState({ notice_on: todaySast(), method: 'whatsapp', note: '' })
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true)
    try {
      await fn()
      toast.success(done)
      setOpen(null)
      onChanged()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <article className="space-y-2 rounded-lg border border-border bg-background p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-foreground">{a.driver ?? 'Driver'}</span>
        <Chip tone={stepTone(a.step)}>Suggested: {a.step_label}</Chip>
      </div>
      <p className="text-xs font-medium text-muted-foreground">{a.what}</p>
      <p className="text-sm text-foreground">{a.allegation}</p>
      {a.evidence && <pre className="whitespace-pre-wrap font-sans text-xs text-muted-foreground">{a.evidence}</pre>}
      <p className="text-xs text-muted-foreground">
        {day(a.period_start)}{a.period_end !== a.period_start ? ` to ${day(a.period_end)}` : ''}{a.clause ? ` · clause ${a.clause}` : ''} · raised {when(a.raised_at)}
        {a.needs_hearing ? ' · above a verbal warning, so a hearing decides it' : ' · his manager decides it after hearing him'}
      </p>
      <div className="flex flex-wrap gap-2 pt-1">
        {a.can_act && a.releasable && open === null && (
          <>
            <Button size="sm" variant="brand" onClick={() => setOpen('release')}><Send className="mr-1 h-3.5 w-3.5" /> Release: I gave him the notice</Button>
            <Button size="sm" variant="outline" onClick={() => setOpen('dismiss')}><X className="mr-1 h-3.5 w-3.5" /> Do not release</Button>
          </>
        )}
        <Button size="sm" variant="ghost" asChild>
          <Link to={a.page}><ExternalLink className="mr-1 h-3.5 w-3.5" /> Open on its own page</Link>
        </Button>
      </div>
      {open === 'release' && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          <p>Give {a.driver ?? 'him'} the allegation above in writing, then record how and when. That starts his time to respond. The decision follows on its own page, after he has been heard.</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Input type="date" max={todaySast()} value={notice.notice_on} onChange={(e) => setNotice((x) => ({ ...x, notice_on: e.target.value }))} aria-label="Given to him on" />
            <NativeSelect value={notice.method} onChange={(e) => setNotice((x) => ({ ...x, method: e.target.value }))} aria-label="How">
              {NOTICE_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </NativeSelect>
          </div>
          <Input value={notice.note} onChange={(e) => setNotice((x) => ({ ...x, note: e.target.value }))} placeholder="Anything to note (optional)" />
          <div className="flex gap-2">
            <Button size="sm" variant="brand" disabled={busy}
              onClick={() => void run(() => release.trigger({ case_id: a.id, ...notice }).result, 'Released. Record his response on its own page.')}>
              {busy && <Spinner className="mr-1 h-3.5 w-3.5" />} Release it
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Not yet</Button>
          </div>
        </div>
      )}
      {open === 'dismiss' && (
        <div className="space-y-2">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why it is not going ahead, for example: the car was in for a repair, or he was not the driver" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={!reason.trim() || busy}
              onClick={() => void run(() => dismiss.trigger({ case_id: a.id, reason }).result, 'Not released. The reason is on the case.')}>Do not release it</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </article>
  )
}
